<template lang="pug">
.pulse365-content
  //- Total Section
  .metrics-section(v-if="totalMetrics.length > 0")
    .section-header
      .section-title {{ $t('settings_tab.agent_tab.dashboard.pulse_configuration.total_section_title') }}
    .metrics-grid
      MetricCard(
        v-for="metricId in totalMetrics"
        :key="metricId"
        :title="getMetricLabel(metricId)"
        :value="getMetricValue(null, metricId)"
        :metricCategory="getMetricCategory(metricId)"
        :metricId="metricId"
        :tooltip="getMetricTooltip(metricId)"
        :loading="loading"
      )

  //- Segment Sections
  .metrics-section(
    v-for="(segment, index) in configuredSegments"
    :key="segment.display_name"
  )
    .section-header
      .section-title {{ segment.display_name }}
    .metrics-grid
      MetricCard(
        v-for="metricId in segment.metrics"
        :key="`${segment.display_name}-${metricId}`"
        :title="getMetricLabel(metricId)"
        :value="getMetricValue(index, metricId)"
        :metricCategory="getMetricCategory(metricId)"
        :metricId="metricId"
        :tooltip="getMetricTooltip(metricId)"
        :loading="loading"
      )

  //- Loading placeholder shown before the first response arrives. Once there
  //- are tiles to draw, each card renders its own spinner instead.
  .loading-state(v-if="loading && !hasAnyMetrics")
    i.el-icon-loading

  //- Empty State, only once loading has settled so it never flashes first.
  .empty-state(v-else-if="!hasAnyMetrics")
    .empty-icon
      i.el-icon-data-analysis
    p.empty-title {{ resolvedEmptyTitle }}
    p.empty-description {{ resolvedEmptyDescription }}
</template>

<script>
import get from 'lodash/get';
import MetricCard from './MetricCard.vue';
import { METRIC_CATEGORIES } from '../constants';

/**
 * Renders a Pulse 360 view: the "All Segments" tiles, one section per
 * configured segment, and the empty state. Shared by the Pulse 360 tab and
 * My Pulse 360 so both always present identical numbers and layout.
 */
export default {
  name: 'Pulse365Grid',
  components: {
    MetricCard,
  },
  props: {
    /** { total_metrics: [...], segments: [{ segment_names, metrics }] } */
    config: {
      type: Object,
      default: null,
    },
    /** { total: {...}, segment_groups: [{ metrics: {...} }] } */
    metricsData: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    emptyTitle: {
      type: String,
      default: '',
    },
    emptyDescription: {
      type: String,
      default: '',
    },
  },
  computed: {
    totalMetrics() {
      return get(this.config, 'total_metrics', []) || [];
    },
    configuredSegments() {
      return (get(this.config, 'segments', []) || [])
        .filter(seg => get(seg, 'segment_names', []).length > 0 && get(seg, 'metrics', []).length > 0)
        .map(seg => ({
          segment_names: seg.segment_names,
          display_name: seg.segment_names.join(', '),
          metrics: seg.metrics,
        }));
    },
    hasAnyMetrics() {
      return this.totalMetrics.length > 0 || this.configuredSegments.length > 0;
    },
    resolvedEmptyTitle() {
      return this.emptyTitle
        || this.$t('settings_tab.agent_tab.dashboard.pulse_configuration.empty_state_title');
    },
    resolvedEmptyDescription() {
      return this.emptyDescription
        || this.$t('settings_tab.agent_tab.dashboard.pulse_configuration.empty_state_description');
    },
  },
  methods: {
    getMetricLabel(metricId) {
      return this.$t(`settings_tab.agent_tab.dashboard.pulse_configuration.metrics.${metricId}`);
    },
    getMetricTooltip(metricId) {
      return this.$t(`settings_tab.agent_tab.dashboard.pulse_configuration.metric_tooltips.${metricId}`);
    },
    getMetricCategory(metricId) {
      return METRIC_CATEGORIES[metricId] || 'count';
    },
    getMetricValue(segmentIndex, metricId) {
      if (segmentIndex === null) {
        return get(this.metricsData, `total.${metricId}.value`, null);
      }
      return get(this.metricsData, `segment_groups.${segmentIndex}.metrics.${metricId}.value`, null);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../../../styles/aiq-variables.scss";

.pulse365-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.metrics-section {
  background: #fff;
  border-radius: 8px;
  padding: 14px 16px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
}

.section-header {
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #ebeef5;
}

.section-title {
  font-size: 15px;
  font-weight: 600;
  color: $aiq-color-text-primary;
  margin-bottom: 0;
}

.metrics-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;

  :deep(.metric-card) {
    flex: 1 1 140px;
  }
}

.loading-state {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  background: #fff;
  border-radius: 16px;
  font-size: 32px;
  color: #409eff;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  background: #fff;
  border-radius: 16px;
  padding: 40px;
  text-align: center;

  .empty-icon {
    font-size: 48px;
    color: #dcdfe6;
    margin-bottom: 16px;
  }

  .empty-title {
    font-size: 18px;
    font-weight: 600;
    color: $aiq-color-text-primary;
    margin-bottom: 8px;
  }

  .empty-description {
    font-size: 14px;
    color: $aiq-color-text-muted;
    max-width: 400px;
  }
}
</style>

<style lang="scss">
@import "../../../../styles/aiq-variables-dark.scss";

[data-theme="dark"] .pulse365-content {
  .metrics-section {
    background: $aiq-bgc-sidebar-item-hover;
    box-shadow: none;
    border: 1px solid $aiq-bdc-input-default;

    .section-header {
      border-bottom-color: $aiq-bdc-input-default;
    }

    .section-title {
      color: $aiq-color-black;
    }
  }

  .loading-state {
    background: $aiq-bgc-sidebar-item-hover;
    border: 1px solid $aiq-bdc-input-default;
  }

  .empty-state {
    background: $aiq-bgc-sidebar-item-hover;
    border: 1px solid $aiq-bdc-input-default;

    .empty-title {
      color: $aiq-color-black;
    }

    p {
      color: $aiq-color-text-muted;
    }
  }
}
</style>
